import axios from 'axios';
import toast from 'react-hot-toast';

import { getUserData } from 'common/getUserData';
import { beginApiRequest, endApiRequest } from 'utils/apiLoadingStore';
import { triggerSessionExpired } from 'utils/sessionExpiredStore';

const BASE_URL = process.env.REACT_APP_EDOC_API_URL || '';

/** Keys that belong on the axios config, not HTTP headers or JSON bodies. */
const AXIOS_OPTION_KEYS = new Set([
  'responseType',
  'timeout',
  'params',
  'onDownloadProgress',
  'onUploadProgress',
  'signal',
  'withCredentials',
]);

function splitAxiosOptions(obj) {
  if (!obj || typeof obj !== 'object') {
    return { rest: {}, axiosParts: {} };
  }
  const rest = {};
  const axiosParts = {};
  Object.keys(obj).forEach((k) => {
    if (AXIOS_OPTION_KEYS.has(k)) {
      axiosParts[k] = obj[k];
    } else {
      rest[k] = obj[k];
    }
  });
  return { rest, axiosParts };
}

export default function getAxiosInstance(link, method, payload, headers) {
  const userData = getUserData();
  const accessToken = userData?.accessToken;
  const isAuthEndpoint = typeof link === 'string' && link.startsWith('auth/');
  const shouldAttachAuthHeader = Boolean(accessToken) && !isAuthEndpoint;

  const { rest: headerFields, axiosParts: axiosFromHeaders } =
    splitAxiosOptions(headers);
  const m = (method || 'get').toLowerCase();

  const baseHeaders = {
    ...(shouldAttachAuthHeader ? { Authorization: `Bearer ${accessToken}` } : {}),
    'app-name': process.env.REACT_APP_NAME,
    'app-version': process.env.REACT_APP_VERSION,
    ...headerFields,
  };

  const run = (axiosConfig) => {
    beginApiRequest();
    return axios(axiosConfig)
      .then((res) => res)
      .catch((err) => {
        console.log(err);
        const statusCode = err?.response?.status;
        const errorMessage = err?.response?.data?.message || '';
        const isTokenErrorMessage =
          typeof errorMessage === 'string' &&
          /token type is invalid|token|jwt|unauthorized|expired/i.test(
            errorMessage
          );
        const isSessionExpired =
          !isAuthEndpoint &&
          (statusCode === 401 || statusCode === 403 || isTokenErrorMessage);

        if (isSessionExpired) {
          localStorage.removeItem('userData');
          toast.dismiss();
          endApiRequest();
          triggerSessionExpired();
          return new Promise(() => {});
        }
        return Promise.reject(err);
      })
      .finally(() => {
        endApiRequest();
      });
  };

  if (m === 'get') {
    const { rest: queryParams, axiosParts: axiosFromPayload } =
      splitAxiosOptions(payload);
    const mergedParams = {
      ...queryParams,
      ...(axiosFromPayload.params || {}),
    };
    const { params: _drop, ...axiosPayloadRest } = axiosFromPayload;
    return run({
      method: 'get',
      url: `${BASE_URL}/${link}`,
      headers: baseHeaders,
      params: mergedParams,
      ...axiosFromHeaders,
      ...axiosPayloadRest,
    });
  }

  if (payload instanceof FormData) {
    return run({
      method,
      url: `${BASE_URL}/${link}`,
      headers: baseHeaders,
      data: payload,
      ...axiosFromHeaders,
    });
  }

  const { rest: bodyData, axiosParts: axiosFromPayload } =
    splitAxiosOptions(payload);

  return run({
    method,
    url: `${BASE_URL}/${link}`,
    headers: baseHeaders,
    data: bodyData,
    ...axiosFromHeaders,
    ...axiosFromPayload,
  });
}
